import React, { useState } from 'react';
import { SparklesIcon, HeartIcon, CheckCircleIcon, CloseIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function ShopSection({ onOpenContact }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');

  const products = [
    {
      id: 'alphabet-blocks',
      title: 'Telugu & Tamil Alphabet Wooden Blocks',
      category: 'Early Language & Tactile Play',
      price: '$28',
      age: 'Ages 1–5',
      badge: 'Bestseller',
      badgeColor: 'bg-amber-100 text-amber-800',
      img: '/assets/images/animal-cow.png',
      desc: 'Solid beechwood blocks engraved with Telugu (అ, ఆ) and Tamil (அ, ஆ) scripts, paired with friendly animal illustrations.',
      details: 'Set of 24 heirloom-quality wooden blocks with rounded baby-safe edges and non-toxic water-based inks.'
    },
    {
      id: 'counting-planks',
      title: 'Symbol Bridge Wooden Counting Planks',
      category: 'Hands-on Early Maths',
      price: '$24',
      age: 'Ages 3–7',
      badge: 'Maths Favorite',
      badgeColor: 'bg-sky-100 text-sky-800',
      img: '/assets/images/treehouse-room.png',
      desc: 'Tactile wooden bridge planks and number frames modeled directly after Miss Kiki’s Symbol Bridge in the math app.',
      details: 'Includes 20 numbered grooved planks, 10-frame tray, and 10 counting beads for intuitive visual addition.'
    },
    {
      id: 'kiki-plush',
      title: 'Miss Kiki Huggable Plush Doll',
      category: 'Plush & Companions',
      price: '$22',
      age: 'All Ages',
      badge: 'Loved by Kids',
      badgeColor: 'bg-rose-100 text-rose-800',
      img: '/assets/images/kiki-real.png',
      desc: '12-inch super-soft cuddle buddy with Miss Kiki’s sunny yellow dress, friendly smile, and embroidered eyes.',
      details: 'Hypoallergenic plush fabric, fully machine-washable, with embroidered details that are 100% safe for infants.'
    },
    {
      id: 'muddle-plush',
      title: 'Muddle the Playful Storm Friend',
      category: 'Plush & Companions',
      price: '$19',
      age: 'All Ages',
      badge: 'New Release',
      badgeColor: 'bg-purple-100 text-purple-800',
      img: '/assets/images/shop-plush.png',
      desc: 'The lovable mischief-maker! Fluffy cloud-soft plush with cute lightning tufts and a gentle interior chime.',
      details: 'Super fluffy, lightweight companion that kids love to cuddle during storytime and Muddle battle levels.'
    },
    {
      id: 'first-words-cards',
      title: 'Bilingual First Words Flashcard Box',
      category: 'Flashcards & Phonics',
      price: '$16',
      age: 'Ages 1–5',
      badge: 'Preschool Essential',
      badgeColor: 'bg-emerald-100 text-emerald-800',
      img: '/assets/images/shop-books.png',
      desc: '50 thick, wipe-clean cards featuring everyday items in English, Telugu, Tamil, and English phonetic pronunciation guides.',
      details: 'Durable tear-proof rounded cards in a magnetic closure box with QR code for native speaker audio pronunciation.'
    },
    {
      id: 'playhome-kit',
      title: 'Playhome Family Starter Learning Box',
      category: 'Curated All-In-One Box',
      price: '$68',
      age: 'Ages 2–6',
      badge: 'Complete Bundle',
      badgeColor: 'bg-indigo-100 text-indigo-800',
      img: '/assets/images/logo.png',
      desc: 'The complete home learning kit: alphabet blocks + counting planks + Miss Kiki plush + flashcards + parent activity guide.',
      details: 'Best value gift box for families wanting the full Kiki and Kids physical and digital learning experience.'
    }
  ];

  const handleOrderClick = (product) => {
    sounds.playSound('pop');
    setSelectedProduct(product);
    setShowOrderModal(true);
    setOrderSent(false);
  };

  const handleSendOrder = (e) => {
    e.preventDefault();
    if (buyerName && buyerEmail) {
      sounds.playSound('chest');
      setOrderSent(true);
      setCartCount(c => c + 1);
    }
  };

  return (
    <section id="shop" className="py-16 lg:py-24 bg-[#FFFDF9] text-slate-800 border-t-2 border-slate-200 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-100 text-rose-800 font-heading font-bold text-xs uppercase tracking-wider mb-2">
              <SparklesIcon className="w-4 h-4 text-rose-600" />
              <span>Official Kiki &amp; Kids Merchandise</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 tracking-tight">
              Kiki and Kids Shop
            </h2>
            <p className="text-slate-600 font-medium text-base sm:text-lg mt-1 max-w-2xl">
              Bringing the magic of the videos and maths app into little hands. Thoughtfully crafted toys, wooden manipulatives, and bilingual books.
            </p>
          </div>

          {/* Cart Status / Inquiry button */}
          <div className="flex items-center gap-3">
            <div className="px-4 py-2.5 rounded-2xl bg-white border-2 border-slate-200 shadow-xs flex items-center gap-2">
              <span className="text-xl">🛍️</span>
              <span className="font-heading font-bold text-xs text-slate-700">Inquiry Bag:</span>
              <span className="px-2 py-0.5 rounded-full bg-kiki-coral text-white font-bold text-xs">
                {cartCount}
              </span>
            </div>
          </div>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((item) => (
            <div
              key={item.id}
              className="rounded-3xl bg-white border-2 border-slate-200/90 p-6 flex flex-col justify-between hover:border-kiki-yellow transition-all shadow-md hover:shadow-xl group"
            >
              <div>
                {/* Header row: badge + age */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-heading font-bold ${item.badgeColor}`}>
                    {item.badge}
                  </span>
                  <span className="text-xs font-bold text-slate-400">
                    {item.age}
                  </span>
                </div>

                {/* Product Image preview */}
                <div className="w-full h-48 rounded-2xl bg-slate-50 border border-slate-100 p-4 mb-4 flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform">
                  <img src={item.img} alt={item.title} className="max-h-full max-w-full object-contain filter drop-shadow-sm" />
                </div>

                {/* Product details */}
                <span className="text-xs font-bold text-kiki-blue uppercase tracking-wider">
                  {item.category}
                </span>
                <h3 className="font-heading font-bold text-xl text-slate-900 mt-1 mb-2 group-hover:text-kiki-coral transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed mb-4">
                  {item.desc}
                </p>
              </div>

              {/* Price & Buy Action */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <span className="text-xs text-slate-400 font-bold block">PRICE</span>
                  <span className="font-heading font-black text-2xl text-slate-900">{item.price}</span>
                </div>
                <button
                  onClick={() => handleOrderClick(item)}
                  className="btn-bouncy px-5 py-2.5 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-heading font-bold text-xs shadow-md flex items-center gap-1.5"
                >
                  <span>Order Inquiry</span>
                  <span>→</span>
                </button>
              </div>

            </div>
          ))}
        </div>

        {/* Trust & Safe Materials Guarantee */}
        <div className="mt-16 rounded-[36px] bg-amber-50/70 border-3 border-amber-200 p-8 sm:p-10 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
            <div className="space-y-1">
              <span className="text-3xl block mb-2">🪵 🌱</span>
              <h4 className="font-heading font-bold text-base text-amber-950">FSC Certified Wood</h4>
              <p className="text-xs text-amber-900/80 font-medium leading-relaxed">
                Smooth natural beechwood, finished with non-toxic water-based food-safe vegetable dyes.
              </p>
            </div>
            <div className="space-y-1">
              <span className="text-3xl block mb-2">🛡️ 👶</span>
              <h4 className="font-heading font-bold text-base text-amber-950">Child Safety Tested</h4>
              <p className="text-xs text-amber-900/80 font-medium leading-relaxed">
                Meets and exceeds ASTM F963 and EN71 European toy safety standards for small children.
              </p>
            </div>
            <div className="space-y-1">
              <span className="text-3xl block mb-2">📦 🌏</span>
              <h4 className="font-heading font-bold text-base text-amber-950">Family Shipping</h4>
              <p className="text-xs text-amber-900/80 font-medium leading-relaxed">
                Shipped carefully to families worldwide. Free shipping on orders over $50.
              </p>
            </div>
          </div>
        </div>

        {/* Order Inquiry Modal */}
        {showOrderModal && selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-pop">
            <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border-4 border-kiki-yellow space-y-5">
              
              <div className="flex items-center justify-between border-b pb-3">
                <span className="text-xs font-heading font-bold text-amber-600 uppercase tracking-wider">
                  Item Order &amp; Availability Inquiry
                </span>
                <button
                  onClick={() => { sounds.playSound('tap'); setShowOrderModal(false); }}
                  className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                >
                  <CloseIcon className="w-6 h-6" />
                </button>
              </div>

              {!orderSent ? (
                <form onSubmit={handleSendOrder} className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-2xl border">
                    <img src={selectedProduct.img} alt={selectedProduct.title} className="w-16 h-16 object-contain rounded-xl" />
                    <div>
                      <h4 className="font-heading font-bold text-base text-slate-900">{selectedProduct.title}</h4>
                      <span className="text-xs font-black text-kiki-coral">{selectedProduct.price}</span>
                      <p className="text-[11px] text-slate-500 mt-0.5">{selectedProduct.details}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Your Name</label>
                      <input
                        type="text"
                        required
                        placeholder="Parent or Teacher name"
                        value={buyerName}
                        onChange={(e) => setBuyerName(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-kiki-yellow"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Email Address</label>
                      <input
                        type="email"
                        required
                        placeholder="For stock availability & shipping estimate"
                        value={buyerEmail}
                        onChange={(e) => setBuyerEmail(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-kiki-yellow"
                      />
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-500 leading-normal">
                    Items are hand-packaged in small batches. We will email you current stock status, shipping times to your region, and direct checkout link.
                  </p>

                  <button
                    type="submit"
                    className="btn-bouncy w-full py-3.5 rounded-2xl bg-kiki-coral hover:bg-rose-500 text-white font-heading font-bold text-sm shadow-md"
                  >
                    Submit Order Inquiry
                  </button>
                </form>
              ) : (
                <div className="py-6 text-center space-y-3 animate-pop">
                  <div className="text-5xl">🎁 ✨</div>
                  <h4 className="font-heading font-black text-2xl text-slate-900">
                    Thank you, {buyerName}!
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-600 font-medium">
                    We received your inquiry for <strong>{selectedProduct.title}</strong>. 
                    Our team will reply to <em>{buyerEmail}</em> within 24 hours with availability and dispatch information.
                  </p>
                  <button
                    onClick={() => { sounds.playSound('tap'); setShowOrderModal(false); }}
                    className="btn-bouncy px-6 py-2.5 rounded-2xl bg-slate-900 text-white font-heading font-bold text-xs shadow-md mt-2"
                  >
                    Continue Browsing
                  </button>
                </div>
              )}

            </div>
          </div>
        )}

      </div>
    </section>
  );
}
